import React, { useState, useEffect } from 'react';
import './Account.scss';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback, GlobalContext } from 'smart-react';
import { Card } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Form } from "@progress/kendo-react-form";
import { buildNotification, GenerateNotification } from "smart-react";
import { EVENTS_DATA_TYPES, NOTIFICATION_TYPES } from "../../../constants/eventDataTypes";
import { FloatingLabel, Label } from "@progress/kendo-react-labels";
import { TextBox } from "@progress/kendo-react-inputs";
import { Upload } from '@progress/kendo-react-upload';
import { getUser, updateAccount } from '../Services/UserService';

const Account = () => {    
    // State to hold form input values and image URL
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        bugzillaApiToken: ''
    });
    const [imageURL, setImageURL] = useState(null);
    const [isImageChanged, setIsImageChanged] = useState(false);

    /**
   * Fetch user data from the API when the component mounts
   */
    useEffect(() => {
        ( async () => {
            try {
                const response = await getUser();
                if (response?.success) {
                    // Set form fields with the data received from the API
                    setFormData({
                        email: response.data.email,
                        name: response.data.username,
                        bugzillaApiToken: response.data.detail.bugzilla_api_token
                    });
                    // Set image URL with the data received from the API
                    if (response.data.detail.profile_image) {
                        setImageURL(response.data.detail.profile_image);
                    }
                } else {
                    GenerateNotification(
                        buildNotification({
                            title: "Failed to fetch user data",
                            description: response.statusText,
                            style: "error"
                        }),
                        NOTIFICATION_TYPES.APP,
                        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                    );
                }
            } catch (error) {
                GenerateNotification(
                    buildNotification({
                        title: "Error fetching user data",
                        description: error,
                        style: "error"
                    }),
                    NOTIFICATION_TYPES.APP,
                    EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                );
            }
        })();
    }, []);

    /**
   * Handle form input changes
   */
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    /**
   * Function to handle image upload
   */
    const handleUpload = (event) => {
        const file = event.newState[0].getRawFile(); // Get the uploaded file
        const fileReader = new FileReader();
        fileReader.onload = () => {
            setImageURL(fileReader.result); // Set image URL in state
            setIsImageChanged(true);
        };
        fileReader.readAsDataURL(file);
    };

    /**
   * Function to handle form submission
   */
    const handleSubmit = async (event) => {
        event.preventDefault(); 

        const dataToSubmit = {
            ...formData,
            userImage: isImageChanged ? imageURL : formData.profile_image 
        };

        try {
            const response = await updateAccount(dataToSubmit);
            if (response.success) {
                // Handle successful submission
                GenerateNotification(
                    buildNotification({
                        title: "Successfully Saved",
                        description: "Your changes have been saved successfully.",
                        style: "success"
                    }),
                    NOTIFICATION_TYPES.APP,
                    EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                );
            } else {
                // Handle server errors
                GenerateNotification(
                    buildNotification({
                        title: "Failed to submit form",
                        description: response.statusText,
                        style: "error"
                    }),
                    NOTIFICATION_TYPES.APP,
                    EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
                );
            }
        } catch (error) {
            // Handle network or other errors
            console.error('Error submitting form:', error);
            GenerateNotification(
                buildNotification({
                    title: "Error submitting form",
                    description: error,
                    style: "error"
                }),
                NOTIFICATION_TYPES.APP,
                EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
            );
        }
    };

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={() => {
                // Reset the state of your app so the error doesn't happen again
            }}
        >
            <div className="container mx-auto account-form-container">
                <div className="add-test add-test-page cards-layout-page container mx-auto">
                    <div className="card-detail-content">
                        <div className="card-title-header">
                            <div className="card-title-wrapper">
                                <div className="action-buttons-container"></div>
                            </div>
                        </div>
                        <Card className="seprate-card">
                            <Form
                                onSubmit={handleSubmit}
                                render={(formRenderProps) => (
                                    <div className="card-content account-form-card">
                                        {/* Form fields */}
                                        <div className='account-form-wrapper' >
                                        <div className="k-d-flex single-field-row">
                                            <div className="k-p-2 field-wrapper">
                                                <FloatingLabel
                                                    label={"Email"}
                                                    editorId={"email"}
                                                    editorValue={formData.email}
                                                >
                                                    <TextBox
                                                        id={"email"}
                                                        name={"email"}
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                    />
                                                </FloatingLabel>
                                            </div>
                                        </div>
                                        <div className="k-d-flex single-field-row">
                                            <div className="k-p-2 field-wrapper">
                                                <FloatingLabel
                                                    label={"Name"}
                                                    editorId={"name"}
                                                    editorValue={formData.name}
                                                >
                                                    <TextBox
                                                        id={"name"}
                                                        name={"name"}
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                    />
                                                </FloatingLabel>
                                            </div>
                                        </div>
                                        <div className="k-d-flex single-field-row">
                                            <div className="k-p-2 field-wrapper">
                                                <FloatingLabel
                                                    label={"Bugzilla Api Token"}
                                                    editorId={"bugzillaApiToken"}
                                                    editorValue={formData.bugzillaApiToken}
                                                >
                                                    <TextBox
                                                        id={"bugzillaApiToken"}
                                                        name={"bugzillaApiToken"}
                                                        value={formData.bugzillaApiToken}
                                                        onChange={handleChange}
                                                    />
                                                </FloatingLabel>
                                            </div>
                                        </div>
                                        <div className="k-d-flex single-field-row">
                                            <div className="k-p-2 field-wrapper">
                                                <Label editorId={"user_image"}>Change Image</Label>
                                                <Upload
                                                    id={"user_image"}
                                                    name={"user_image"}
                                                    multiple={false}
                                                    allowedExtensions={[".jpg", ".png"]}
                                                    onAdd={handleUpload}
                                                />
                                            </div>
                                        </div>
                                        <div className="k-d-flex single-field-row button-form-wrapper">
                                            <Button
                                                themeColor={"primary"}
                                                className="k-mx-1"
                                                type="submit"
                                                onClick={handleSubmit}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                        </div>
                                        <div className='account-image-display'>
                                        <div className="k-d-flex single-field-row">
                                            <div className="k-p-2 field-wrapper">
                                            <h4>Image Preview</h4>
                                                {imageURL && (
                                                    <img src={imageURL} alt="Uploaded" className="uploaded-image" />
                                                )}
                                            </div>
                                        </div>
                                        </div>
                                        
                                    </div>
                                )}
                            />
                        </Card>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default Account;
